import type { CareOrder } from '@talos/kyoko';

export interface CareOrderNettingValidationResult {
  nettable: boolean;
  reason?: string;
}

/**
 * Given an array of Care Orders, will check that a few basic properties all line up and can be netted together.
 * If something doesn't line up, nettable: false is returned with a user-friendly csv string of failure reasons to render
 */
export function validateNetCareOrders(careOrders: CareOrder[]): CareOrderNettingValidationResult {
  const reasons: string[] = [];

  if (careOrders.length < 2) {
    reasons.push('Select at least 2 Care Orders to Net');
  }

  if (careOrders.some(careOrder => !careOrder.canBeNetted)) {
    reasons.push('Only New non-netted Care Orders can be Netted');
  }

  if (!allCareOrdersShareProperty(careOrders, careOrder => careOrder.Currency)) {
    reasons.push('Care Orders must have the same Currency to be Netted');
  }

  if (!allCareOrdersShareProperty(careOrders, careOrder => careOrder.SettleDate)) {
    reasons.push('Care Orders must have the same SettleDate to be Netted');
  }

  if (!allCareOrdersShareProperty(careOrders, careOrder => careOrder.Symbol)) {
    reasons.push('Care Orders must have the same Symbol to be Netted');
  }

  if (reasons.length > 0) {
    return {
      nettable: false,
      reason: reasons.join(', '),
    };
  }

  return {
    nettable: true,
  };
}

function allCareOrdersShareProperty<T>(
  careOrders: CareOrder[],
  propertySelector: (careOrder: CareOrder) => T
): boolean {
  if (careOrders.length < 2) {
    return true;
  }

  const [first, ...rest] = careOrders;
  const firstValue = propertySelector(first);
  return rest.every(careOrder => propertySelector(careOrder) === firstValue);
}
